import { stateToPayload } from '@aviata/booking-payload-mapper';
import { getBrowserInfo } from '@aviata/browser-info';
import { useRouter, useRoute } from 'vue-router';
import { providerClassLib, providerClassConfig } from 'shared';
import { useQueryClient } from '@tanstack/vue-query';
import type { Airflow } from '@aviata/platform-types';
import type { AxiosError } from 'axios';

import { seatMapLib } from '@/widgets/seat-map';
import { roamingConfig } from '@/widgets/roaming';
import { passengersFormLib } from '@/widgets/passengers-form';

import { api } from '@/shared/api';
import { sentryModel } from '@/shared/model';
import {
  analyticsEventsLib,
  acceleraEventsLib,
  extractAnalyticsDataLib,
} from '@/shared/lib';
import type { amplitudeEventsConfigTypes } from '@/shared/config';

import { mapPassengers } from '../lib';

export function useBookingFlow({
  offerId,
  products,
  booking,
  bookingStore,
  modal,
  vuelidate,
  ancillariesStore,
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const route = useRoute();

  function updateProductsStateAtBookingStore() {
    const productsState = {
      products: products.products.value,
      state: products.state.value,
    };

    bookingStore.update({
      ancillaries: ancillariesStore.ancillaries?.state,
      products: productsState,
      packageInfo: products.packageInfo.value,
      roaming: products.isRoamingSelected.value
        ? products.products.value.filter(
            product => product.service === roamingConfig.ROAMING,
          )
        : [],
    });
  }

  function goToPaymentPage(orderId: unknown, showPriceChangeToast: boolean) {
    router.push({
      name: 'PaymentPage',
      params: {
        orderId: String(orderId),
      },
      query: {
        showPriceChangeToast: String(showPriceChangeToast),
        source: 'products_page',
      },
    });
  }

  function submit() {
    vuelidate.value.$touch();

    if (!vuelidate.value.$invalid) {
      const validatingAirlines = Object.keys(
        bookingStore.passengersSchema?.passengers?.schema?.['ADT-0']?.schema
          ?.airline_bonus_cards?.schema ?? {},
      );
      const validatingAirlinesString = validatingAirlines.join(',');

      const hasAirlineBonusCards = Object.values(
        bookingStore.state.passengers,
      ).some(passenger =>
        passengersFormLib.milesCardsStorageLib.getMilesCardNumberByPassengerId(
          passenger.id,
        ),
      );

      const payload = stateToPayload({
        offerId,
        confirmWarning: booking.isWarningConfirmed.value,
        cancelPrevBooking: booking.shouldCancelPrevBooking.value,
        passengers: mapPassengers(
          bookingStore.state.passengers,
          providerClassLib.isFunSun(bookingStore.offer?.$meta?.provider_class),
          validatingAirlines,
        ),
        contacts: bookingStore.state.contacts,
        airlines: validatingAirlines,
        ancillaries: bookingStore.state.ancillaries,
        products: bookingStore.state.products.state,
        selectedSeats:
          bookingStore.state.seatMap &&
          seatMapLib.preparePayload(bookingStore.state.seatMap),
      });

      /**
       * Adds roaming and mct products to the payload.
       * Combines existing products with selected roaming and mct products.
       * Only product IDs are included in the final array of roaming products.
       * Product IDs and additional data like passengers idx and their passport_issue_date
       * are included in the final array if there are mct selected products.
       */
      payload.products = [
        ...payload.products,
        ...(bookingStore.state.roaming?.map(product => ({ id: product.id })) ||
          []),
        ...bookingStore.medicalInsuranceProducts.map(
          product => product?.payload,
        ),
        ...(bookingStore.flightDelayProducts ?? []).map(p => ({ id: p.id })),
      ];

      booking
        .book(payload)
        .then(data => {
          const source = route.params
            .source as amplitudeEventsConfigTypes.DetailsPageOpenSource;

          // Extract refundable from order response (may be updated during booking flow)
          // Ticket product is always at index 0
          const refundableFromOrder = (
            data.products?.[0] as Airflow.Product.V2.AirflowBooking
          )?.data?.offer?.features?.refundable?.value;

          // Get current segments data from bookingStore (after fare family change)
          // This ensures baggage/hand_luggage info is up-to-date
          const segmentsData =
            bookingStore.mappedFlights?.length &&
            bookingStore.dict &&
            bookingStore.offer?.$meta?.search_query
              ? extractAnalyticsDataLib.getSegmentsDataFromMappedFlights(
                  bookingStore.mappedFlights,
                  bookingStore.dict,
                  bookingStore.offer.$meta.search_query,
                )
              : undefined;

          analyticsEventsLib.bookingSuccess({
            airlineBonusCards: hasAirlineBonusCards
              ? validatingAirlinesString
              : '',
            orderId: data.id,
            orderNumber: data.number,
            packageInfo: bookingStore.state.packageInfo,
            products: bookingStore.state.products.state,
            seatMap: bookingStore.state.seatMap,
            baggage: bookingStore.state.ancillaries,
            medicalInsurance: bookingStore.medicalInsuranceProducts.length > 0,
            roaming: bookingStore.state.roaming?.length > 0,
            source,
            total: data.total,
            providerClass: bookingStore.offer?.$meta?.provider_class ?? '',
            refundable: refundableFromOrder,
            connectType: bookingStore.offer?.$meta?.connect_type,
            segmentsData,
            isDomestic: bookingStore.offer?.$meta?.is_domestic,
            searchQuery: bookingStore.offer?.$meta?.search_query,
          });

          api.ecommerce.sendBrowserInfo(data.id, getBrowserInfo('mobile'));

          bookingStore.update({
            roaming: [],
          });
          /*
           * price_change has the same structure, but for products its located in data obj,
           * while for offer price_change obj located in data.offer
           * */
          const hasPriceChanged = data?.products.some(
            (bookingProduct: Airflow.Product.V2.AirflowBooking) => {
              if (bookingProduct?.data?.offer) {
                return bookingProduct?.data?.offer?.price_change?.some(
                  change => Number(change?.difference) > 0,
                );
              }
              return bookingProduct?.data?.price_change?.some(
                change => Number(change?.difference) > 0,
              );
            },
          );

          goToPaymentPage(data.id, hasPriceChanged);
        })
        .catch((error: AxiosError) => {
          const status = error?.status;
          if (status && status >= 500 && status < 600) {
            acceleraEventsLib.bookingFailed(error?.message);
          }
        });

      sentryModel.addBreadcrumb({
        category: sentryModel.breadcrumbCategories.BOOKING,
        message: 'submit: start booking',
        level: sentryModel.level.INFO,
      });
    }
  }

  // Since revenue page open is sent only when all products are loaded,
  // there is a handle for case where user didn't wait for products to load.
  const sendIfNotSentRevenuePageOpen = () => {
    if (
      !sessionStorage.getItem(
        analyticsEventsLib.config.sessionStorageAnalyticsKeys.bloomreach,
      )
    ) {
      const checkSeatMapQuery = queryClient.getQueryData([
        'seatMapCheck',
        offerId,
      ]);
      sessionStorage.setItem(
        analyticsEventsLib.config.sessionStorageAnalyticsKeys.bloomreach,
        'true',
      );
      analyticsEventsLib.productsPageOpen(
        {
          products: products.products.value,
          packages: products.packages.value,
          isBaggageAvailable: ancillariesStore.isBaggageAvailable,
          isSeatMapAvailable: Boolean(checkSeatMapQuery?.available),
        },
        bookingStore.offer?.$meta?.provider_class ?? '',
      );
    }
  };

  function onSubmit() {
    sendIfNotSentRevenuePageOpen();
    updateProductsStateAtBookingStore();
    submit();
  }

  function onCancelPrevBooking() {
    booking.shouldCancelPrevBooking.value = true;
    submit();
    modal.hide('booking-double-booking-modal');

    sentryModel.addBreadcrumb({
      category: sentryModel.breadcrumbCategories.BOOKING,
      message: 'previous booking canceled',
      level: sentryModel.level.INFO,
    });
  }

  function onWarningConfirm() {
    if (booking.warnings.value.length > 1) {
      booking.warnings.value = booking.warnings.value.slice(1);
    } else {
      booking.onWarningConfirm();
      submit();
    }
    modal.hide('booking-warning-modal');

    sentryModel.addBreadcrumb({
      category: sentryModel.breadcrumbCategories.BOOKING,
      message: 'warning confirmed',
      level: sentryModel.level.INFO,
    });
  }
  return {
    onSubmit,
    goToPaymentPage,
    onCancelPrevBooking,
    onWarningConfirm,
    updateProductsStateAtBookingStore,
  };
}
